
// @ts-nocheck
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

@Component
export default class CodeInput extends Vue {
  @Prop({ default: () => [] }) code!: Array<string>;
  @Prop({ default: () => () => {} }) success!: Function;
  @Prop({ default: () => () => {} }) error!: Function;
  @Prop({ default: () => () => {} }) getInput!: Function;
  @Prop({ default: '20px' }) spanSize!: string;
  @Prop({ default: '#3C4043' }) spanColor!: string;
  @Prop({ default: '20px' }) inputSize!: string;
  @Prop({ default: '#3C4043' }) inputColor!: string;
  @Prop({ default: '40px' }) height!: string;
  @Prop({ default: 6 }) number!: number;
  @Prop({
    default: 'text',
    validator(value: any): boolean {
      return ['number', 'text'].indexOf(value) > -1;
    }
  })
  type!: String;
  @Prop({ default: true }) upperCase!: boolean;

  private inputCodeNum = 0;
  private codeArray: Array<string> = [];
  private block: Array<number> = [];
  private isSyncingInternal = false;

  get blockWidth(): string {
    return (100 / this.number).toFixed(4) + '%';
  }

  get inputType(): string {
    return this.type === 'number' ? 'tel' : 'text';
  }

  get inputMode(): string {
    return this.type === 'number' ? 'numeric' : 'text';
  }

  get codeString(): string {
    return this.codeArray.join('');
  }

  get isComplete(): boolean {
    return this.codeArray.filter(Boolean).length === this.number;
  }

  created() {
    this.initializeState();
  }

  mounted() {
    this.$nextTick(() => {
      this.focusInput(0);
    });
  }

  @Watch('number')
  onNumberChange() {
    this.initializeState();
  }

  @Watch('code', { deep: true })
  onExternalCodeChange(newValue: Array<string>) {
    if (this.isSyncingInternal) {
      return;
    }
    this.syncFromExternal(newValue);
  }

  focusInput(index?: number) {
    let targetIndex = index;
    if (typeof targetIndex !== 'number') {
      const firstEmpty = this.codeArray.findIndex(item => !item);
      targetIndex = firstEmpty > -1 ? firstEmpty : this.number - 1;
    }
    this.focusCell(Math.min(Math.max(targetIndex, 0), this.number - 1));
  }

  onFocus(index: number) {
    this.inputCodeNum = index;
  }

  onBlur() {
    this.inputCodeNum = -1;
  }

  onInput(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    const sanitized = this.sanitizeInput(target.value).slice(0, 1);
    if (!sanitized && target.value) {
      this.handleError(target.value);
      target.value = '';
      return;
    }
    this.setValueAt(index, sanitized);
    target.value = sanitized;
    if (sanitized && index < this.number - 1) {
      this.focusNext(index);
    }
    this.emitInput();
    if (this.isComplete) {
      this.handleSuccess();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace') {
      if (this.codeArray[index]) {
        this.setValueAt(index, '');
      } else if (index > 0) {
        const prevIndex = index - 1;
        this.setValueAt(prevIndex, '');
        this.focusCell(prevIndex);
      }
      this.emitInput();
      event.preventDefault();
      return;
    }
    if (event.key === 'Delete') {
      this.setValueAt(index, '');
      this.emitInput();
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowLeft') {
      this.focusPrevious(index);
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowRight') {
      this.focusNext(index);
      event.preventDefault();
      return;
    }
  }

  onPaste(event: ClipboardEvent, index: number) {
    const clipboard = event.clipboardData?.getData('text') || '';
    const sanitized = this.sanitizeInput(clipboard);
    if (!sanitized) {
      this.handleError(clipboard);
      return;
    }
    sanitized.split('').forEach((char, offset) => {
      const targetIndex = index + offset;
      if (targetIndex >= this.number) return;
      this.setValueAt(targetIndex, char);
    });
    this.emitInput();
    if (this.isComplete) {
      this.handleSuccess();
      return;
    }
    const nextIndex = Math.min(index + sanitized.length, this.number - 1);
    this.focusCell(nextIndex);
  }

  private initializeState() {
    this.block = [];
    for (let i = 0; i < this.number; i++) {
      this.block.push(i);
    }
    this.codeArray = Array.from({ length: this.number }, (_, idx) => {
      const value = this.code[idx] || '';
      return this.sanitizeInput(value).slice(0, 1);
    });
    this.inputCodeNum = 0;
  }

  private syncFromExternal(externalCode: Array<string>) {
    this.codeArray = Array.from({ length: this.number }, (_, idx) => {
      const value = externalCode[idx] || '';
      return this.sanitizeInput(value).slice(0, 1);
    });
    const firstEmpty = this.codeArray.findIndex(item => !item);
    this.inputCodeNum = firstEmpty > -1 ? firstEmpty : this.number - 1;
  }

  private focusCell(index: number) {
    this.inputCodeNum = index;
    this.$nextTick(() => {
      const inputs = this.getInputRefs();
      inputs[index]?.focus();
      inputs[index]?.select?.();
    });
  }

  private focusNext(index: number) {
    if (index >= this.number - 1) {
      return;
    }
    this.focusCell(index + 1);
  }

  private focusPrevious(index: number) {
    if (index <= 0) {
      return;
    }
    this.focusCell(index - 1);
  }

  private setValueAt(index: number, value: string) {
    this.$set(this.codeArray, index, value);
    this.syncCodeProp();
  }

  private syncCodeProp() {
    const filtered = this.codeArray.filter(item => !!item);
    this.isSyncingInternal = true;
    this.code.splice(0, this.code.length, ...filtered);
    this.$nextTick(() => {
      this.isSyncingInternal = false;
    });
  }

  private sanitizeInput(value: string): string {
    if (!value) return '';
    const pattern = this.type === 'number' ? /[0-9]/g : /[A-Za-z0-9]/g;
    const matched = value.match(pattern);
    if (!matched) return '';
    const sanitized = matched.join('');
    return this.upperCase ? sanitized.toUpperCase() : sanitized;
  }

  private emitInput() {
    this.getInput(this.codeString);
  }

  private handleSuccess() {
    this.success(this.codeString);
    const inputs = this.getInputRefs();
    inputs.forEach(input => input.blur());
  }

  private handleError(value: string) {
    this.error(value);
  }

  private getInputRefs(): HTMLInputElement[] {
    const refs = this.$refs.codeInput;
    if (!refs) return [];
    return Array.isArray(refs) ? (refs as HTMLInputElement[]) : [refs as HTMLInputElement];
  }
}
